import DynamicMultiSelectInput, {
    Props as DynamicMultiSelectInputProps,
} from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import { ReactNode } from 'react';
import { FieldValues } from 'react-hook-form';
import { useLastNameFirst } from 'utils/nameOrder';
import { PartyNameParty, renderPartyNumberAndIcon } from 'utils/partyNameUtil';
import { buildContactEndAdornment } from './buildContactEndAdornment';
import { renderContactTags } from './renderContactTags';
import { resolveContactOptionLabel } from './resolveContactOptionLabel';

/**
 * Contact-aware wrapper around `DynamicMultiSelectInput`. Party number + type icon on the start,
 * `buildContactEndAdornment` on the end (combined with any caller-supplied extra adornment).
 */
type ContactDynamicMultiSelectInputProps<
    TFormData extends FieldValues,
    TOption extends FieldValues & PartyNameParty,
> = Omit<
    DynamicMultiSelectInputProps<TFormData, TOption>,
    'getOptionStartAdornment' | 'getOptionEndAdornment' | 'getOptionLabel' | 'getOptionIdentifier'
> & {
    /** Defaults to `"name"`. */
    getOptionLabel?: DynamicMultiSelectInputProps<TFormData, TOption>['getOptionLabel'];
    /** Defaults to `"id"`. */
    getOptionIdentifier?: DynamicMultiSelectInputProps<TFormData, TOption>['getOptionIdentifier'];
    /** Optional extra end adornment (e.g. `renderPartyEmail`). Rendered BEFORE the PNG chip. */
    getOptionEndAdornment?: (option: TOption) => ReactNode;
};

const ContactDynamicMultiSelectInput = <TFormData extends FieldValues, TOption extends FieldValues & PartyNameParty>(
    props: ContactDynamicMultiSelectInputProps<TFormData, TOption>
) => {
    const {
        getOptionEndAdornment: callerEndAdornment,
        getOptionLabel = 'name' as DynamicMultiSelectInputProps<TFormData, TOption>['getOptionLabel'],
        getOptionIdentifier = 'id' as DynamicMultiSelectInputProps<TFormData, TOption>['getOptionIdentifier'],
        renderTags: callerRenderTags,
        ...rest
    } = props;

    const getOptionEndAdornment = (option: TOption): ReactNode => {
        const extra = callerEndAdornment ? callerEndAdornment(option) : null;
        return buildContactEndAdornment(option, extra);
    };

    const lastNameFirst = useLastNameFirst();
    const resolveLabel = (option: TOption): string | null =>
        resolveContactOptionLabel(option, getOptionLabel as string | ((o: TOption) => string), lastNameFirst);

    return (
        <DynamicMultiSelectInput
            {...rest}
            getOptionLabel={getOptionLabel}
            getOptionIdentifier={getOptionIdentifier}
            getOptionStartAdornment={renderPartyNumberAndIcon}
            getOptionEndAdornment={getOptionEndAdornment}
            renderTags={
                callerRenderTags ?? ((value, getTagProps) => renderContactTags(resolveLabel, value, getTagProps))
            }
        />
    );
};

export default ContactDynamicMultiSelectInput;
